import React from 'react';
import { Link } from 'react-router-dom';
import pageStyles from './MarketingPage.module.css';
import sectionStyles from '../components/marketing/sections/sections.module.css';
import ExpandableCard from '../components/marketing/ExpandableCard';
import FinalCTASection from '../components/marketing/sections/FinalCTASection';
import { HELP_FAQ, HELP_START } from '../components/marketing/helpContent';
import { trackEvent } from '../utils/analytics';
import useDocumentMeta from '../hooks/useDocumentMeta';
import useReveal from '../hooks/useReveal';

const SHOP_URL = 'https://veloxco.com';
const CONTACT_EMAIL = 'info@velox-network.com';

const HelpPage = () => {
  const revealRef = useReveal();

  useDocumentMeta({
    title: 'Help',
    description:
      'How to get started with Velox, how the contactless card works, and answers to common questions about profiles, teams and privacy.',
    path: '/help',
  });

  return (
    <div ref={revealRef}>
      <section className={pageStyles.pageHero}>
        <div className="vxm-grid-motif" />
        <div className={`vxm-container vxm-container--narrow ${pageStyles.pageHeroInner}`}>
          <p className="vxm-eyebrow">Help</p>
          <h1 className="vxm-h1">Get set up, then get back to the room</h1>
          <p className="vxm-lead">
            Short answers for starting with Velox, using a card, and what a
            company account can and cannot see.
          </p>
        </div>
      </section>

      <section className="vxm-section">
        <div className="vxm-container vxm-container--narrow">
          <div className="vxm-reveal">
            <h2 className="vxm-h2">Getting started</h2>
          </div>
          <ol className={pageStyles.detailList} style={{ marginTop: 'var(--vxm-space-6)' }}>
            {HELP_START.map((step, index) => (
              <li key={step.title} className={`${pageStyles.detailRow} vxm-reveal`}>
                <h3 className={pageStyles.detailTerm}>
                  {index + 1}. {step.title}
                </h3>
                <p className={pageStyles.detailBody}>{step.body}</p>
              </li>
            ))}
          </ol>
          <p className="vxm-body vxm-reveal" style={{ marginTop: 'var(--vxm-space-6)' }}>
            <Link to="/how-it-works" className="vxm-link">
              How Velox works, in more detail
            </Link>
          </p>
        </div>
      </section>

      <section className="vxm-section vxm-section--raised">
        <div className="vxm-container vxm-container--narrow">
          <div className="vxm-section-head vxm-reveal">
            <h2 className="vxm-h2">Questions</h2>
          </div>
          <div className={sectionStyles.featureGroups}>
            {HELP_FAQ.map((item) => (
              <div key={item.question} className="vxm-reveal">
                <ExpandableCard title={item.question} headingLevel="h3">
                  <p className="vxm-body">{item.answer}</p>
                </ExpandableCard>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="vxm-section">
        <div className="vxm-container vxm-container--narrow">
          <div className={`${pageStyles.panel} vxm-reveal`}>
            <h2 className={pageStyles.panelTitle}>Cards, privacy, and us</h2>
            <ul className={sectionStyles.featureList} style={{ marginTop: 'var(--vxm-space-4)' }}>
              <li className={sectionStyles.featureItem}>
                <span>
                  Order a standard card at{' '}
                  <a
                    href={SHOP_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="vxm-link"
                    onClick={() => trackEvent('Marketing CTA', 'shop_click', 'help')}
                  >
                    veloxco.com
                  </a>
                  . Custom artwork is arranged with our design team.
                </span>
              </li>
              <li className={sectionStyles.featureItem}>
                <span>
                  <Link to="/security-privacy" className="vxm-link">
                    Security and privacy
                  </Link>
                  {' '}sets out what an employer can and cannot see.
                </span>
              </li>
              <li className={sectionStyles.featureItem}>
                <span>
                  Still stuck?{' '}
                  <a href={`mailto:${CONTACT_EMAIL}`} className="vxm-link">
                    {CONTACT_EMAIL}
                  </a>
                </span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      <FinalCTASection
        title="Create your profile"
        body="Start with Velox today. Add a card when you want one, or tell us about a team."
        source="help_final"
      />
    </div>
  );
};

export default HelpPage;
